/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { UsersApi } from '../apis';
import { AuthContext } from '../contexts';
import { AuthUserProps } from '../../config/interfaces';

/**
 * Redux slice for managing authentication-related state using Redux Toolkit's `createSlice`.
 *
 * This slice handles actions related to user authentication, session management, and user roles, including:
 * - Handling authentication success and failure
 * - Storing the authorized user
 * - Clearing user authentication state
 * - Setting user identifiers and session IDs
 *
 * @property {object} initialState - The initial state for the authentication context.
 * @property {Function} clearUser - Clears the current user and related authentication state.
 * @property {Function} setUser34 - Sets the user34 identifier in the state.
 * @property {Function} setSessionId - Sets the session ID in the state.
 *
 * @example
 * dispatch(setSessionId('1234-5678-90ab-cdef12345678'));
 *
 * @returns {object} The auth slice reducer and actions.
 */
const authSlice = createSlice({
  name: 'auth',
  initialState: AuthContext,
  reducers: {
    // TODO: Keeping this for now for testing
    getAuthorizedUserSuccess: (state, action: PayloadAction<any>) => {
      return {
        ...state,
        authorizedUser: action.payload,
        authRoles: action.payload.roles,
        employeesDelegatedTo:
          action.payload.employeeInfo?.employeesDelegatedTo ?? []
      };
    },
    clearUser: state => {
      return {
        ...state,
        authorizedUser: {} as AuthUserProps,
        user34: '',
        authRoles: [],
        employeesDelegatedTo: []
      };
    },
    setUser34: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        user34: action.payload
      };
    },
    setSessionId: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        sessionId: action.payload
      };
    }
  },
  extraReducers: builder => {
    builder.addMatcher(
      UsersApi.endpoints.getAuthorizedUser.matchFulfilled,
      (state, action) => ({
        ...state,
        authorizedUser: action.payload,
        authRoles: action.payload.roles,
        employeesDelegatedTo:
          action.payload.employeeInfo?.employeesDelegatedTo ?? []
      })
    );
    builder.addMatcher(
      UsersApi.endpoints.getAuthorizedUser.matchRejected,
      (state, action) => ({
        ...state,
        authorizedUser: {} as AuthUserProps,
        authRoles: [],
        employeesDelegatedTo: []
      })
    );
  }
});

export const { clearUser, setUser34, setSessionId } = authSlice.actions;

export default authSlice.reducer;
